/**
 * Email HTML for display in ONE: sanitised with DOMPurify (no scripts, forms, embeds or event handlers), links open
 * in a new tab, and remote images are blocked until the reader allows them — like Outlook, so senders can't track
 * when an email was opened.
 */
import DOMPurify from "dompurify";

const FORBID_TAGS = ["style", "script", "form", "input", "button", "textarea", "select", "option", "iframe", "frame", "frameset", "object", "embed", "link", "meta", "base", "svg", "math", "audio", "video"];

export interface CleanEmail {
  html: string;
  blockedImages: number;
}

export function cleanEmailHtml(raw: string, allowImages: boolean): CleanEmail {
  const purify = DOMPurify(window);
  let blockedImages = 0;
  purify.addHook("afterSanitizeAttributes", (node) => {
    const el = node as Element;
    if (el.tagName === "A") {
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer nofollow");
    }
    if (el.tagName === "IMG") {
      const src = el.getAttribute("src") ?? "";
      if (/^cid:/i.test(src)) {
        el.remove(); // inline attachment images can't be shown outside Outlook
        return;
      }
      if (/^https?:/i.test(src) && !allowImages) {
        el.removeAttribute("src");
        el.removeAttribute("srcset");
        el.setAttribute("alt", el.getAttribute("alt") || "[image]");
        el.setAttribute("data-blocked", "1");
        blockedImages++;
      }
    }
    // background images in inline styles are tracking pixels too
    const style = el.getAttribute("style");
    if (style && !allowImages && /url\s*\(/i.test(style)) {
      el.setAttribute("style", style.replace(/[a-z-]*\s*:[^;]*url\s*\([^)]*\)[^;]*;?/gi, ""));
      blockedImages++;
    }
    el.removeAttribute("background");
  });
  const html = purify.sanitize(raw, { FORBID_TAGS, FORBID_ATTR: ["srcdoc", "formaction"], ALLOW_DATA_ATTR: false, ADD_ATTR: ["target"] }) as string;
  return { html, blockedImages };
}

/** First words of an email, as plain text (for collapsed messages). */
export function emailSnippet(raw: string, max = 140): string {
  const div = document.createElement("div");
  div.innerHTML = DOMPurify.sanitize(raw.replace(/<\/(p|div|li|tr|h[1-6])>|<br\s*\/?>/gi, "$& "), { FORBID_TAGS: [...FORBID_TAGS, "img"] }) as string;
  return (div.textContent ?? "").replace(/\s+/g, " ").trim().slice(0, max);
}
